import api from "./api";

// Enum matching backend MaskingRuleScope
export enum MaskingRuleScope {
  Global = 0,
  Server = 1,
  ServerTool = 2,
}

export interface CreateMaskingRuleDto {
  name: string;
  description?: string;
  fieldPath: string;
  keepFirstChars: number;
  keepLastChars: number;
  maskingSymbol: string;
  priority: number;
  scope: MaskingRuleScope;
  serverId?: string;
  toolName?: string;
}

export interface UpdateMaskingRuleDto {
  name?: string;
  description?: string;
  fieldPath?: string;
  keepFirstChars?: number;
  keepLastChars?: number;
  maskingSymbol?: string;
  isActive?: boolean;
  priority?: number;
}

export interface MaskingRuleDto {
  id: string;
  name: string;
  description?: string;
  fieldPath: string;
  keepFirstChars: number;
  keepLastChars: number;
  maskingSymbol: string;
  isActive: boolean;
  isSuggested: boolean;
  priority: number;
  scope: MaskingRuleScope;
  serverId?: string;
  serverName?: string;
  toolName?: string;
  createdAt: string;
  updatedAt: string;
  /** Set when the suggestion was dismissed. Null/undefined means it is still pending. */
  dismissedAt?: string | null;
}

export interface TestMaskingDto {
  jsonData: string;
  serverId?: string;
  toolName?: string;
}

export interface TestMaskingResultDto {
  originalJson: string;
  maskedJson: string;
  appliedRules: string[];
}

export const getMaskingRules = async (params?: {
  scope?: string;
  serverId?: string;
}): Promise<MaskingRuleDto[]> => {
  const response = await api.get("/masking", { params });
  return response.data;
};

export const getMaskingRuleById = async (
  id: string,
): Promise<MaskingRuleDto> => {
  const response = await api.get(`/masking/${id}`);
  return response.data;
};

export const createMaskingRule = async (
  data: CreateMaskingRuleDto,
): Promise<MaskingRuleDto> => {
  const response = await api.post("/masking", data);
  return response.data;
};

export const updateMaskingRule = async (
  id: string,
  data: UpdateMaskingRuleDto,
): Promise<MaskingRuleDto> => {
  const response = await api.put(`/masking/${id}`, data);
  return response.data;
};

export const deleteMaskingRule = async (id: string): Promise<void> => {
  await api.delete(`/masking/${id}`);
};

export const testMasking = async (
  data: TestMaskingDto,
): Promise<TestMaskingResultDto> => {
  const response = await api.post("/masking/test", data);
  return response.data;
};

/**
 * Lists masking rule suggestions awaiting a decision.
 *
 * Dismissed suggestions are excluded unless `includeDismissed` is set — the Suggestions page passes
 * it so an admin can find and restore something they dismissed by mistake, while the topbar banner
 * count relies on the default.
 */
export const getSuggestedMaskingRules = async (
  serverId?: string,
  includeDismissed = false,
): Promise<MaskingRuleDto[]> => {
  const params: Record<string, string | boolean> = {};
  if (serverId) params.serverId = serverId;
  if (includeDismissed) params.includeDismissed = true;
  const response = await api.get("/masking/suggested", { params });
  return response.data;
};

export const approveSuggestedRules = async (
  ruleIds: string[],
): Promise<MaskingRuleDto[]> => {
  const response = await api.post("/masking/suggested/approve", { ruleIds });
  return response.data;
};

/**
 * Dismisses suggestions. They are retained, not deleted, so `restoreSuggestedRules` can undo this.
 */
export const declineSuggestedRules = async (
  ruleIds: string[],
): Promise<number> => {
  const response = await api.post("/masking/suggested/decline", { ruleIds });
  return response.data.dismissed;
};

/** Returns dismissed suggestions to the pending list. The undo for `declineSuggestedRules`. */
export const restoreSuggestedRules = async (
  ruleIds: string[],
): Promise<number> => {
  const response = await api.post("/masking/suggested/restore", { ruleIds });
  return response.data.restored;
};
